import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchLabourBreakdown, fetchCoExpenses } from "../api";
import {
  PageHeader, Card, Section, Stat, Field, Select, SplitBar,
  Loading, ErrorState, EmptyState, useHorizontalScrollShadow,
} from "../components/ui";
import {
  sortPeriods, classifyRow, getBucket, availableHours, calcUtil,
  DENOMINATOR_DEDUCTION_PROJECTS, heatClass,
} from "../lib/periods";
import {
  money0, moneyCompact, hrs, hrs2, pct, fmtPeriod, fmtPeriodShort, surname, num,
} from "../lib/format";
import { readUi, writeUi } from "../lib/persist";

const C_DIRECT   = "var(--s-1)";
const C_INDIRECT = "var(--warn)";
const C_UNAVAIL  = "var(--neg)";

/** A whole year is picked from the same selector as a month, tagged like this. */
const YEAR_PREFIX = "Y:";
const isYearSel  = (v) => String(v).startsWith(YEAR_PREFIX);
const yearOf     = (v) => String(v).replace(YEAR_PREFIX, "");
const periodYear = (p) => String(p).split("/")[1];

/* ── Project x employee matrix ─────────────────────────────────────────── */
/**
 * Project rows against an arbitrary column axis. In month mode the columns are
 * the people who booked time; in year mode they are the months of the year, so
 * the same table answers "who did this work" and "when did it happen" without
 * needing two components.
 */
function BreakdownMatrix({ rows, columns, colKeyOf, emptyMsg, accent, revenueByProject = {} }) {
  const scrollRef = useHorizontalScrollShadow();

  const projects = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => {
      if (!seen.has(r.project_code)) seen.set(r.project_code, r.project_name || r.project_code);
    });
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [rows]);

  const matrix = useMemo(() => {
    const m = {};
    rows.forEach((r) => {
      if (!m[r.project_code]) m[r.project_code] = {};
      const k = colKeyOf(r);
      m[r.project_code][k] = (m[r.project_code][k] || 0) + num(r.hours);
    });
    return m;
  }, [rows, colKeyOf]);

  const hasRevenue = Object.keys(revenueByProject).length > 0;
  const grandTotal = rows.reduce((s, r) => s + num(r.hours), 0);
  const totalRev = Object.values(revenueByProject).reduce((s, v) => s + v, 0);

  if (!projects.length) return <div className="state">{emptyMsg}</div>;

  return (
    <div className="tbl-wrap tbl-wrap--flush" ref={scrollRef}>
      <table className="tbl">
        <thead>
          <tr>
            <th className="sticky-col" style={{ minWidth: 240 }}>Project</th>
            {columns.map((c) => (
              <th key={c.key} className="num" title={c.title}>{c.label}</th>
            ))}
            <th className="num" style={{ color: accent }}>Total</th>
            {hasRevenue && <th className="num">Revenue</th>}
          </tr>
        </thead>
        <tbody>
          {projects.map(([code, name]) => {
            const rowTotal = columns.reduce((s, c) => s + (matrix[code]?.[c.key] || 0), 0);
            const rev = revenueByProject[name];
            return (
              <tr key={code}>
                <td className="sticky-col">
                  <span className="tree-label">
                    <span className="code-tag">{code}</span>
                    <span className="truncate" title={name}>{name}</span>
                  </span>
                </td>
                {columns.map((c) => {
                  const h = matrix[code]?.[c.key];
                  return (
                    <td key={c.key} className="num">
                      {h ? hrs(h) : <span className="dash">—</span>}
                    </td>
                  );
                })}
                <td className="num" style={{ fontWeight: 650, color: accent }}>{hrs(rowTotal)}</td>
                {hasRevenue && (
                  <td className="num">
                    {rev ? money0(rev) : <span className="dash">—</span>}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td className="sticky-col">Total</td>
            {columns.map((c) => {
              const t = projects.reduce((s, [code]) => s + (matrix[code]?.[c.key] || 0), 0);
              return <td key={c.key} className="num">{t ? hrs(t) : "—"}</td>;
            })}
            <td className="num" style={{ color: accent }}>{hrs(grandTotal)}</td>
            {hasRevenue && <td className="num">{money0(totalRev)}</td>}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────────────────────── */
export default function Dashboard({ username }) {
  const [breakdown, setBreakdown] = useState([]);
  const [revenue, setRevenue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [period, setPeriod] = useState("");
  const isFd = String(username).toLowerCase() === "fd";
  const [manager, setManager] = useState(() => readUi("dash.manager", "All"));
  const [employee, setEmployee] = useState(() => readUi("dash.employee", "All"));

  const load = React.useCallback(() => {
    setLoading(true);
    setError("");
    Promise.all([fetchLabourBreakdown(), fetchCoExpenses()])
      .then(([rows, coExps]) => {
        setBreakdown(rows || []);
        setRevenue((coExps || []).filter((r) => r.account_name === "Revenue"));
        const ps = sortPeriods([...new Set((rows || []).map((r) => r.period))]);
        // Open on the latest full year: the side-by-side comparison is the point
        // of the page, and a single month is the thing you narrow down to.
        setPeriod((cur) => {
          if (cur && (isYearSel(cur) || ps.includes(cur))) return cur;
          return ps[0] ? `${YEAR_PREFIX}${periodYear(ps[0])}` : "";
        });
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);
  useEffect(() => { writeUi("dash.manager", manager); }, [manager]);
  useEffect(() => { writeUi("dash.employee", employee); }, [employee]);

  const periods = useMemo(
    () => sortPeriods([...new Set(breakdown.map((r) => r.period))]),
    [breakdown]
  );

  const years = useMemo(
    () => [...new Set(periods.map(periodYear))].sort((a, b) => b - a),
    [periods]
  );

  const isYear = isYearSel(period);
  /** Newest first everywhere else; months read left to right in the tables. */
  const yearMonths = useMemo(
    () => (isYear
      ? sortPeriods(periods.filter((p) => periodYear(p) === yearOf(period))).slice().reverse()
      : []),
    [isYear, periods, period]
  );

  const managers = useMemo(
    () => [...new Set(breakdown.map((r) => r.partner).filter(Boolean))].sort(),
    [breakdown]
  );

  /**
   * Narrowing to a manager has to reach the hours *and* the revenue, or the
   * revenue-per-hour tile would divide one manager's hours into everybody's
   * fees. Labour is keyed by `partner`, revenue by `username`.
   */
  const managerRows = useMemo(
    () => (manager === "All" ? breakdown : breakdown.filter((r) => r.partner === manager)),
    [breakdown, manager]
  );

  const scoped = useMemo(
    () => (employee === "All"
      ? managerRows
      : managerRows.filter((r) => r.employee_code === employee)),
    [managerRows, employee]
  );

  const scopedRevenue = useMemo(
    () => (manager === "All" ? revenue : revenue.filter((r) => r.username === manager)),
    [revenue, manager]
  );

  /** The picker offers the selected manager's team, not everybody. */
  const teamEmployees = useMemo(() => {
    const seen = new Map();
    managerRows.forEach((r) => {
      if (!seen.has(r.employee_code)) seen.set(r.employee_code, r.employee_name);
    });
    return [...seen.entries()]
      .map(([code, name]) => ({ employee_code: code, employee_name: name }))
      .sort((a, b) => a.employee_name.localeCompare(b.employee_name));
  }, [managerRows]);

  useEffect(() => {
    if (employee !== "All" && !teamEmployees.some((e) => e.employee_code === employee)) {
      setEmployee("All");
    }
  }, [teamEmployees, employee]);

  const employees = useMemo(() => {
    const seen = new Map();
    scoped.forEach((r) => {
      if (!seen.has(r.employee_code)) seen.set(r.employee_code, r.employee_name);
    });
    return [...seen.entries()]
      .map(([code, name]) => ({ employee_code: code, employee_name: name }))
      .sort((a, b) => a.employee_name.localeCompare(b.employee_name));
  }, [scoped]);

  const periodRows = useMemo(
    () => (isYear
      ? scoped.filter((r) => periodYear(r.period) === yearOf(period))
      : scoped.filter((r) => r.period === period)),
    [scoped, period, isYear]
  );

  /**
   * Per employee, and within that per month. Available hours have to be worked
   * out a month at a time even for a year total - a month is the unit the
   * working-day count and the leave deduction are defined on. Only months an
   * employee actually appears in count towards their denominator, which is what
   * keeps a part-year joiner from reading as permanently under-utilised.
   */
  const rows = useMemo(() => {
    const byEmp = {};
    periodRows.forEach((r) => {
      const emp = (byEmp[r.employee_code] ||= {
        employee_code: r.employee_code,
        employee_name: r.employee_name,
        partner: r.partner,
        direct: 0, indirect: 0, deduction: 0, total: 0,
        months: {},
      });
      const m = (emp.months[r.period] ||= { direct: 0, indirect: 0, deduction: 0, denomDeduction: 0 });
      const c = classifyRow(r);
      emp.direct += c.direct;
      emp.indirect += c.indirect;
      emp.deduction += c.deduction;
      emp.total += c.total;
      m.direct += c.direct;
      m.indirect += c.indirect;
      m.deduction += c.deduction;
      if (r.labor_code === "NB" && DENOMINATOR_DEDUCTION_PROJECTS.has(r.project_code)) {
        m.denomDeduction += num(r.hours);
      }
    });
    return Object.values(byEmp)
      .map((e) => {
        let avail = 0;
        for (const [p, m] of Object.entries(e.months)) {
          m.avail = availableHours(p, m.denomDeduction);
          avail += m.avail;
        }
        return { ...e, avail };
      })
      .sort((a, b) => a.employee_name.localeCompare(b.employee_name));
  }, [periodRows]);

  const kpi = useMemo(() => {
    const acc = { direct: 0, indirect: 0, deduction: 0, total: 0, avail: 0 };
    rows.forEach((r) => {
      acc.direct += r.direct;
      acc.indirect += r.indirect;
      acc.deduction += r.deduction;
      acc.total += r.total;
      acc.avail += r.avail;
    });
    acc.util = calcUtil(acc.direct, acc.avail);
    return acc;
  }, [rows]);

  /** Does this period belong to what is currently selected? */
  const inScope = useCallback(
    (p) => (isYear ? periodYear(p) === yearOf(period) : p === period),
    [isYear, period]
  );

  const periodRevenue = useMemo(
    () => scopedRevenue.filter((r) => inScope(r.period)).reduce((s, r) => s + num(r.amount), 0),
    [scopedRevenue, inScope]
  );

  /**
   * Revenue is booked to a project and a manager, never to a person, so once a
   * single employee is selected there is no honest way to show it against
   * their hours - a whole project's fee over one person's time is a number
   * that means nothing. It is withheld rather than quietly mis-stated.
   */
  const revenueApplies = employee === "All";

  const revenueByProject = useMemo(
    () =>
      !revenueApplies ? {} :
      scopedRevenue
        .filter((r) => inScope(r.period))
        .reduce((acc, r) => {
          if (r.project_name) acc[r.project_name] = (acc[r.project_name] || 0) + num(r.amount);
          return acc;
        }, {}),
    [scopedRevenue, inScope, revenueApplies]
  );

  const directRows = useMemo(() => periodRows.filter((r) => r.labor_code !== "NB"), [periodRows]);
  const indirectRows = useMemo(
    () => periodRows.filter((r) => r.labor_code === "NB" && getBucket(r.project_code) === "indirect"),
    [periodRows]
  );
  const deductionRows = useMemo(
    () => periodRows.filter((r) => r.labor_code === "NB" && getBucket(r.project_code) === "deduction"),
    [periodRows]
  );

  const periodEmployees = useMemo(() => {
    const codes = new Set(periodRows.map((r) => r.employee_code));
    return employees.filter((e) => codes.has(e.employee_code));
  }, [employees, periodRows]);

  /**
   * A month shows the work split across the people who did it; a year shows it
   * spread across the months, which is the comparison a year is actually for.
   */
  const matrixColumns = useMemo(
    () => (isYear
      ? yearMonths.map((p) => ({ key: p, label: fmtPeriodShort(p), title: fmtPeriod(p) }))
      : periodEmployees.map((e) => ({
          key: e.employee_code, label: surname(e.employee_name), title: e.employee_name,
        }))),
    [isYear, yearMonths, periodEmployees]
  );

  const matrixColKey = useCallback(
    (r) => (isYear ? r.period : r.employee_code),
    [isYear]
  );

  /** Team utilisation per month, for the year view's footer row. */
  const teamByMonth = useMemo(() => {
    const t = {};
    for (const p of yearMonths) t[p] = { direct: 0, avail: 0 };
    for (const r of rows) {
      for (const [p, m] of Object.entries(r.months)) {
        if (!t[p]) continue;
        t[p].direct += m.direct;
        t[p].avail += m.avail;
      }
    }
    return t;
  }, [rows, yearMonths]);

  const showPartnerCol = isFd && manager === "All";
  const scopeLabel = isYear ? `${yearOf(period)} full year` : fmtPeriod(period);
  /**
   * What each month did, on the same definitions as the cards above: direct
   * hours over available hours for utilisation, revenue over worked hours for
   * the rate. Aggregated across whoever is in scope, so an employee filter
   * narrows these too.
   *
   * Revenue is booked to a project and a manager, never to a person, so it is
   * withheld once a single employee is selected - the same rule the cards use.
   */
  const monthStats = useMemo(() => {
    const out = {};
    for (const p of yearMonths) {
      out[p] = { direct: 0, indirect: 0, deduction: 0, avail: 0, revenue: 0 };
    }
    for (const e of rows) {
      for (const [p, m] of Object.entries(e.months)) {
        const s = out[p];
        if (!s) continue;
        s.direct += m.direct; s.indirect += m.indirect;
        s.deduction += m.deduction; s.avail += m.avail || 0;
      }
    }
    if (revenueApplies) {
      for (const r of scopedRevenue) {
        if (out[r.period]) out[r.period].revenue += num(r.amount);
      }
    }
    for (const s of Object.values(out)) {
      s.worked = s.direct + s.indirect;
      s.logged = s.worked + s.deduction;
      s.util = calcUtil(s.direct, s.avail);
      // A rate needs both halves. Revenue and hours currently cover different
      // years, so this is null far more often than it looks like it should be.
      s.revPerHour = s.worked > 0 && s.revenue > 0 ? s.revenue / s.worked : null;
    }
    return out;
  }, [rows, yearMonths, scopedRevenue, revenueApplies]);

  /** The rows of that strip: label, how to render a month, and the year total. */
  const MONTH_METRICS = [
    { key: "util",  label: "Utilisation",
      cell: (s) => (s.avail > 0 ? `${s.util.toFixed(1)}%` : "—"),
      total: () => `${kpi.util.toFixed(1)}%`,
      cls: (s) => (s.avail <= 0 ? "muted"
        : s.util >= 80 ? "ms-good" : s.util >= 60 ? "ms-warn" : "ms-bad") },
    { key: "rev",   label: "Revenue",
      cell: (s) => (s.revenue ? money0(s.revenue) : "—"),
      total: () => (revenueApplies && periodRevenue ? money0(periodRevenue) : "—") },
    { key: "rph",   label: "Revenue per hour",
      cell: (s) => (s.revPerHour != null ? money0(s.revPerHour) : "—"),
      total: () => (revenueApplies && revPerHour ? money0(revPerHour) : "—") },
    { key: "dir",   label: "Project hours",
      cell: (s) => (s.direct ? hrs2(s.direct) : "—"), total: () => hrs2(kpi.direct) },
    { key: "ind",   label: "Non-project hours",
      cell: (s) => (s.indirect ? hrs2(s.indirect) : "—"), total: () => hrs2(kpi.indirect) },
    { key: "un",    label: "Unavailable hours",
      cell: (s) => (s.deduction ? hrs2(s.deduction) : "—"), total: () => hrs2(kpi.deduction) },
  ];

  const workedHours = kpi.direct + kpi.indirect;
  const revPerHour = workedHours > 0 ? periodRevenue / workedHours : null;
  const totalLogged = kpi.direct + kpi.indirect + kpi.deduction;
  const share = (n) => (totalLogged > 0 ? pct(n, totalLogged, 0) : "—");
  const utilColour =
    kpi.util >= 80 ? "var(--pos)" : kpi.util >= 60 ? "var(--warn)" : "var(--neg)";

  if (loading) return <main className="page"><Loading label="Loading dashboard…" /></main>;
  if (error) return <main className="page"><ErrorState message={error} onRetry={load} /></main>;

  return (
    <main className="page">
      <PageHeader
        title="Dashboard"
        subtitle={
          period
            ? (manager !== "All" ? `${manager.toUpperCase()} · ` : "")
              + (employee !== "All"
                  ? `${(teamEmployees.find((e) => e.employee_code === employee) || {}).employee_name || employee} · `
                  : "")
              + `${scopeLabel} · ${periodEmployees.length} people · ${hrs(totalLogged)} hours logged`
            : "No timesheet data available"
        }
      >
        {isFd && (
          <Field label="Manager">
            <Select
              value={manager}
              onChange={setManager}
              options={[
                { value: "All", label: "All managers" },
                ...managers.map((m) => ({ value: m, label: m.toUpperCase() })),
              ]}
              style={{ minWidth: 130 }}
            />
          </Field>
        )}
        <Field label="Employee">
          <Select
            value={employee}
            onChange={setEmployee}
            options={[
              { value: "All", label: manager === "All" ? "All employees" : "Whole team" },
              ...teamEmployees.map((e) => ({
                value: e.employee_code, label: e.employee_name,
              })),
            ]}
            style={{ minWidth: 190 }}
          />
        </Field>
        <Field label="Period">
          <Select
            value={period}
            onChange={setPeriod}
            options={[
              ...years.map((y) => ({ value: `${YEAR_PREFIX}${y}`, label: `${y} — full year` })),
              ...periods.map((p) => ({ value: p, label: fmtPeriod(p) })),
            ]}
            style={{ minWidth: 150 }}
          />
        </Field>
      </PageHeader>

      {!period ? (
        <Card><EmptyState title="No timesheet periods" hint="Nothing has been imported yet." /></Card>
      ) : (
        <div className="stack">
          <div className="stat-grid">
            <Stat label="Revenue" accent="var(--violet)"
                  value={revenueApplies ? money0(periodRevenue) : "—"}
                  meta={revenueApplies ? scopeLabel : "booked per project, not per person"} />
            <Stat label="Revenue per hour" accent="var(--info)"
                  value={revenueApplies && revPerHour ? money0(revPerHour) : "—"}
                  meta={!revenueApplies ? "not attributable to one person"
                    : revPerHour ? `over ${hrs(workedHours)} worked hours`
                    : "no revenue booked in this period"} />
            <Stat label="Utilisation" accent={utilColour} value={`${kpi.util.toFixed(1)}%`}
                  meta={`${hrs(kpi.direct)} of ${hrs(kpi.avail)} available`} />
            <Stat label="Project hours" accent={C_DIRECT} value={hrs2(kpi.direct)}
                  share={share(kpi.direct)} meta="Billable client work" />
            <Stat label="Non-project hours" accent={C_INDIRECT} value={hrs2(kpi.indirect)}
                  share={share(kpi.indirect)} meta="Scoping, BD, training, admin" />
            <Stat label="Unavailable hours" accent={C_UNAVAIL} value={hrs2(kpi.deduction)}
                  share={share(kpi.deduction)} meta="Leave, sickness, holidays" />
          </div>

          <Card title="Team summary" note={scopeLabel} flush>
            <div className="tbl-wrap tbl-wrap--flush">
              <table className="tbl tbl-zebra">
                <thead>
                  <tr>
                    {showPartnerCol && <th style={{ width: 70 }}>Partner</th>}
                    <th className="col-flex" style={{ minWidth: 190 }}>Employee</th>
                    {isYear ? (
                      yearMonths.map((p) => (
                        <th key={p} className="num" title={fmtPeriod(p)}>{fmtPeriodShort(p)}</th>
                      ))
                    ) : (
                      <>
                        <th className="num">Project</th>
                        <th className="num">Non-project</th>
                        <th className="num">Unavailable</th>
                        <th style={{ width: 110 }}>Mix</th>
                      </>
                    )}
                    <th className="num" style={{ width: 76 }}>
                      {isYear ? "Year %" : "Util %"}
                    </th>
                  </tr>
                </thead>

                {/* What each month did, sitting directly under its own column
                    heading so the number and the month line up. Year view only:
                    in month view the columns are people, not months. */}
                {isYear && (
                  <tbody className="ms">
                    {MONTH_METRICS.map((m) => (
                      <tr key={m.key} className="ms-row">
                        {showPartnerCol && <td />}
                        <th className="col-flex ms-label">{m.label}</th>
                        {yearMonths.map((p) => {
                          const st = monthStats[p];
                          return (
                            <td key={p} className={`num ${m.cls ? m.cls(st) : ""}`}
                                title={fmtPeriod(p)}>
                              {m.cell(st)}
                            </td>
                          );
                        })}
                        <td className="num ms-total">{m.total()}</td>
                      </tr>
                    ))}
                  </tbody>
                )}

                <tbody>
                  {rows.map((r) => {
                    const util = calcUtil(r.direct, r.avail);
                    return (
                      <tr key={r.employee_code}>
                        {showPartnerCol && (
                          <td><span className="badge">{String(r.partner || "").toUpperCase()}</span></td>
                        )}
                        <td>
                          {/* Clicking the name drives the Employee selector, and
                              clicking the selected person again backs out to the
                              whole team - quicker than reaching for the dropdown. */}
                          <button
                            type="button"
                            className={`emp-pick${employee === r.employee_code ? " is-on" : ""}`}
                            onClick={() =>
                              setEmployee((cur) =>
                                cur === r.employee_code ? "All" : r.employee_code)}
                            title={employee === r.employee_code
                              ? "Back to the whole team"
                              : `Show only ${r.employee_name}`}
                          >
                            {r.employee_name}
                          </button>
                        </td>
                        {isYear ? (
                          yearMonths.map((p) => {
                            const m = r.months[p];
                            if (!m) return <td key={p} className="num"><span className="dash">—</span></td>;
                            const u = calcUtil(m.direct, m.avail);
                            return (
                              <td
                                key={p}
                                className={`num heat ${heatClass(u)}`}
                                title={`${fmtPeriod(p)} · ${hrs(m.direct)}h of ${hrs(m.avail)}h available`}
                              >
                                {u.toFixed(1)}
                              </td>
                            );
                          })
                        ) : (
                          <>
                            <td className="num">{hrs2(r.direct)}</td>
                            <td className="num">{hrs2(r.indirect)}</td>
                            <td className="num">
                              {r.deduction ? hrs2(r.deduction) : <span className="dash">—</span>}
                            </td>
                            <td>
                              <SplitBar
                                title={`Project ${hrs(r.direct)}h · Non-project ${hrs(r.indirect)}h · Unavailable ${hrs(r.deduction)}h`}
                                parts={[
                                  { value: r.direct, color: C_DIRECT },
                                  { value: r.indirect, color: C_INDIRECT },
                                  { value: r.deduction, color: C_UNAVAIL },
                                ]}
                              />
                            </td>
                          </>
                        )}
                        <td className={`num heat ${heatClass(util)}`} style={{ fontWeight: 700 }}>
                          {util.toFixed(1)}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    {showPartnerCol && <td />}
                    <td>Team total</td>
                    {isYear ? (
                      yearMonths.map((p) => {
                        const t = teamByMonth[p];
                        const u = t ? calcUtil(t.direct, t.avail) : 0;
                        return (
                          <td key={p} className={`num heat ${heatClass(u)}`}>
                            {t && t.avail ? u.toFixed(1) : <span className="dash">—</span>}
                          </td>
                        );
                      })
                    ) : (
                      <>
                        <td className="num">{hrs2(kpi.direct)}</td>
                        <td className="num">{hrs2(kpi.indirect)}</td>
                        <td className="num">{hrs2(kpi.deduction)}</td>
                        <td>
                          <SplitBar
                            parts={[
                              { value: kpi.direct, color: C_DIRECT },
                              { value: kpi.indirect, color: C_INDIRECT },
                              { value: kpi.deduction, color: C_UNAVAIL },
                            ]}
                          />
                        </td>
                      </>
                    )}
                    <td className={`num heat ${heatClass(kpi.util)}`}>{kpi.util.toFixed(1)}%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>

          <Section
            title="Project hours by client project"
            accent={C_DIRECT}
            meta={revenueApplies
              ? `${hrs(kpi.direct)} h · ${moneyCompact(periodRevenue)} revenue`
              : `${hrs(kpi.direct)} h`}
          >
            <BreakdownMatrix
              rows={directRows}
              columns={matrixColumns}
              colKeyOf={matrixColKey}
              accent={C_DIRECT}
              revenueByProject={revenueByProject}
              emptyMsg="No project hours booked this period."
            />
          </Section>

          <Section
            title="Non-project hours — scoping, BD, training, admin"
            accent={C_INDIRECT}
            meta={`${hrs(kpi.indirect)} h`}
            defaultOpen={false}
          >
            <BreakdownMatrix
              rows={indirectRows}
              columns={matrixColumns}
              colKeyOf={matrixColKey}
              accent={C_INDIRECT}
              emptyMsg="No non-project hours this period."
            />
          </Section>

          <Section
            title="Unavailable — leave, sickness, public holidays"
            accent={C_UNAVAIL}
            meta={`${hrs(kpi.deduction)} h`}
            defaultOpen={false}
          >
            <BreakdownMatrix
              rows={deductionRows}
              columns={matrixColumns}
              colKeyOf={matrixColKey}
              accent={C_UNAVAIL}
              emptyMsg="No leave or sickness recorded this period."
            />
          </Section>
        </div>
      )}
    </main>
  );
}
